/**
 * Ticket search results (21 Oct): a DataGrid of at most 500 tickets, newest
 * first, sortable by any column, 25 to a page, with a quick filter over the
 * rows already found and an export of all of them to a CSV Excel opens.
 *
 * The number opens the ticket in the Ivanti console (every IT user reads
 * tickets there); a HelpDesk admin also gets the HelpDesk ticket page. The
 * custom-field columns (Build, Entities, Systems, Project, next step, the
 * external reference, the offering) appear only when a row has a value.
 */

import { Download, ExternalLink, Icon, MessageSquareText } from "@norican/icons";
import {
  Alert,
  Badge,
  Button,
  DataGrid,
  type DataGridColumn,
  type DataGridSort,
  Input,
  Pagination,
  TextLink,
  Typography,
} from "@norican/react";
import { useMemo, useState } from "react";
import { Link } from "react-router";

import { CopyLinkMenu, EmptyStateBlock } from "@/components/common";
import { useLanguage, useT } from "@/i18n";
import type { TicketSearchResponse, TicketSearchRow } from "@/lib/api";

import { type CsvColumn, csvFileName, quickFilter, rowsToCsv } from "./criteria";

const PAGE_SIZE = 25;

type ColKey =
  | "number"
  | "type"
  | "subject"
  | "status"
  | "priority"
  | "team"
  | "owner"
  | "customer"
  | "category"
  | "service"
  | "offering"
  | "source"
  | "build"
  | "entities"
  | "systems"
  | "project"
  | "nextStep"
  | "external"
  | "created"
  | "updated"
  | "resolved"
  | "link";

/** Columns shown only when at least one row has a value. */
const OPTIONAL: (keyof TicketSearchRow)[] = [
  "offering",
  "build",
  "entities",
  "systems",
  "project",
  "next_step_at",
  "external_ref",
];

function stamp(value: string | null | undefined): number {
  return value ? Date.parse(value) : 0;
}

function compare(
  left: string | number | null | undefined,
  right: string | number | null | undefined,
): number {
  if (typeof left === "number" && typeof right === "number") {
    return left - right;
  }
  return String(left ?? "").localeCompare(String(right ?? ""), undefined, { numeric: true });
}

function download(text: string, name: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

interface SearchResultsProps {
  result: TicketSearchResponse;
  canOpenInHelpdesk: boolean;
}

export function SearchResults({ result, canOpenInHelpdesk }: SearchResultsProps) {
  const { t } = useT("tickets");
  const { t: tc } = useT();
  const { locale } = useLanguage();
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<DataGridSort | null>(null);
  const [filter, setFilter] = useState("");
  const [exported, setExported] = useState<number | null>(null);

  const day = useMemo(() => new Intl.DateTimeFormat(locale, { dateStyle: "medium" }), [locale]);
  const fmt = (value: string | null | undefined) => (value ? day.format(new Date(value)) : null);
  const kindLabel = (row: TicketSearchRow) =>
    row.kind === "request"
      ? t("itAdmin.ticketSearch.type.request")
      : t("itAdmin.ticketSearch.type.incident");

  const shownOptional = useMemo(
    () => new Set(OPTIONAL.filter((key) => result.items.some((row) => row[key]))),
    [result.items],
  );

  const columns = useMemo<DataGridColumn<TicketSearchRow>[]>(() => {
    const L = (key: ColKey) => t(`itAdmin.ticketSearch.col.${key}`);
    const all: (DataGridColumn<TicketSearchRow> & { optional?: keyof TicketSearchRow })[] = [
      {
        id: "number",
        header: L("number"),
        width: 132,
        sortable: true,
        accessor: (row) => Number(row.number) || row.number,
        cell: (row) => (
          <span className="inline-flex items-center gap-1">
            {row.ivanti_url ? (
              <TextLink
                href={row.ivanti_url}
                target="_blank"
                rel="noreferrer"
                title={t("itAdmin.ticketSearch.openIvanti")}
              >
                {row.number}
              </TextLink>
            ) : (
              row.number
            )}
            {canOpenInHelpdesk && row.url_path ? (
              <TextLink
                render={<Link to={row.url_path} />}
                aria-label={t("itAdmin.ticketSearch.openHelpdesk", { number: row.number })}
                title={t("itAdmin.ticketSearch.openHelpdesk", { number: row.number })}
              >
                <Icon icon={ExternalLink} size="xs" />
              </TextLink>
            ) : null}
            {/* 23 Oct: the ITSM link for colleagues, the HelpDesk link for the requester. */}
            <CopyLinkMenu
              variant="icon"
              kind={row.kind === "request" ? "SERVICE_REQUEST" : "INCIDENT"}
              number={row.number}
              summary={row.subject}
              itsmUrl={row.ivanti_url}
            />
          </span>
        ),
      },
      {
        id: "type",
        header: L("type"),
        width: 96,
        sortable: true,
        accessor: (row) => kindLabel(row),
        cell: (row) => (
          <Badge size="sm" variant="subtle" tone={row.kind === "incident" ? "info" : "accent"}>
            {kindLabel(row)}
          </Badge>
        ),
      },
      {
        id: "subject",
        header: L("subject"),
        width: 340,
        sortable: true,
        accessor: (row) => row.subject,
        cell: (row) => (
          <span className="flex flex-col gap-0.5">
            <span className="line-clamp-2 break-words">{row.subject}</span>
            {row.matched_in_notes ? (
              <span className="text-fg-muted inline-flex items-center gap-1">
                <Icon icon={MessageSquareText} size="xs" />
                <Typography variant="caption-sm" tone="muted">
                  {t("itAdmin.ticketSearch.matchedInNotes")}
                </Typography>
              </span>
            ) : null}
          </span>
        ),
      },
      {
        id: "status",
        header: L("status"),
        width: 150,
        sortable: true,
        accessor: (row) => row.status,
        cell: (row) => (
          <Badge size="sm" variant="subtle" tone={row.is_open ? "warning" : "neutral"}>
            {row.status}
          </Badge>
        ),
      },
      {
        id: "priority",
        header: L("priority"),
        width: 90,
        sortable: true,
        accessor: (row) => row.priority ?? row.urgency ?? null,
      },
      { id: "team", header: L("team"), width: 150, sortable: true, accessor: (row) => row.team },
      { id: "owner", header: L("owner"), width: 140, sortable: true, accessor: (row) => row.owner },
      {
        id: "customer",
        header: L("customer"),
        width: 140,
        sortable: true,
        accessor: (row) => row.customer,
      },
      {
        id: "offering",
        optional: "offering",
        header: L("offering"),
        width: 200,
        sortable: true,
        accessor: (row) => row.offering,
      },
      {
        id: "build",
        optional: "build",
        header: L("build"),
        width: 120,
        sortable: true,
        accessor: (row) => row.build,
      },
      {
        id: "entities",
        optional: "entities",
        header: L("entities"),
        width: 100,
        sortable: true,
        accessor: (row) => row.entities,
      },
      {
        id: "systems",
        optional: "systems",
        header: L("systems"),
        width: 120,
        sortable: true,
        accessor: (row) => row.systems,
      },
      {
        id: "project",
        optional: "project",
        header: L("project"),
        width: 110,
        sortable: true,
        accessor: (row) => row.project,
      },
      {
        id: "nextStep",
        optional: "next_step_at",
        header: L("nextStep"),
        width: 130,
        sortable: true,
        accessor: (row) => stamp(row.next_step_at),
        cell: (row) => fmt(row.next_step_at),
      },
      {
        id: "external",
        optional: "external_ref",
        header: L("external"),
        width: 130,
        sortable: true,
        accessor: (row) => row.external_ref,
      },
      {
        id: "created",
        header: L("created"),
        width: 130,
        sortable: true,
        accessor: (row) => stamp(row.created_at),
        cell: (row) => fmt(row.created_at),
      },
      {
        id: "updated",
        header: L("updated"),
        width: 130,
        sortable: true,
        accessor: (row) => stamp(row.updated_at),
        cell: (row) => fmt(row.updated_at),
      },
    ];
    return all.filter((column) => !column.optional || shownOptional.has(column.optional));
  }, [t, day, canOpenInHelpdesk, shownOptional]);

  const filtered = useMemo(
    () =>
      quickFilter(result.items, filter, (row) => [
        row.number,
        row.subject,
        row.status,
        row.team,
        row.owner,
        row.customer,
        row.offering,
        row.build,
        row.entities,
        row.systems,
        row.project,
        row.external_ref,
      ]),
    [result.items, filter],
  );
  const sorted = useMemo(() => {
    const accessor = columns.find((column) => column.id === sort?.columnId)?.accessor;
    if (sort === null || accessor === undefined) {
      return filtered;
    }
    const direction = sort.direction === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => compare(accessor(a), accessor(b)) * direction);
  }, [filtered, columns, sort]);

  const csvColumns = useMemo<CsvColumn<TicketSearchRow>[]>(() => {
    const L = (key: ColKey) => t(`itAdmin.ticketSearch.col.${key}`);
    return [
      { label: L("number"), value: (r) => r.number },
      { label: L("type"), value: (r) => kindLabel(r) },
      { label: L("subject"), value: (r) => r.subject },
      { label: L("status"), value: (r) => r.status },
      { label: L("priority"), value: (r) => r.priority ?? r.urgency },
      { label: L("team"), value: (r) => r.team },
      { label: L("owner"), value: (r) => r.owner },
      { label: L("customer"), value: (r) => r.customer },
      { label: L("category"), value: (r) => r.category },
      { label: L("service"), value: (r) => r.service },
      { label: L("offering"), value: (r) => r.offering },
      { label: L("source"), value: (r) => r.source },
      { label: L("build"), value: (r) => r.build },
      { label: L("entities"), value: (r) => r.entities },
      { label: L("systems"), value: (r) => r.systems },
      { label: L("project"), value: (r) => r.project },
      { label: L("nextStep"), value: (r) => r.next_step_at?.slice(0, 10) },
      { label: L("external"), value: (r) => r.external_ref },
      { label: L("created"), value: (r) => r.created_at?.slice(0, 16).replace("T", " ") },
      { label: L("updated"), value: (r) => r.updated_at?.slice(0, 16).replace("T", " ") },
      { label: L("resolved"), value: (r) => r.resolved_at?.slice(0, 16).replace("T", " ") },
      { label: L("link"), value: (r) => r.ivanti_url },
    ];
  }, [t]);

  const count = (n: number) => n.toLocaleString(locale);
  const shown = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (result.items.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        <Notes result={result} />
        <EmptyStateBlock title={t("itAdmin.ticketSearch.results.empty")} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Typography variant="heading-5" as="h2">
          {t("itAdmin.ticketSearch.results.count", { count: result.items.length })}
        </Typography>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            size="sm"
            className="w-64"
            value={filter}
            placeholder={t("itAdmin.ticketSearch.results.quickFilter")}
            aria-label={t("itAdmin.ticketSearch.results.quickFilter")}
            onChange={(event) => {
              setFilter(event.target.value);
              setPage(1);
            }}
          />
          <Button
            size="sm"
            variant="outline"
            startIcon={<Icon icon={Download} size="sm" />}
            onClick={() => {
              download(rowsToCsv(sorted, csvColumns), csvFileName(new Date()));
              setExported(sorted.length);
            }}
          >
            {t("itAdmin.ticketSearch.results.export")}
          </Button>
        </div>
      </div>
      {result.capped ? (
        <Alert tone="warning">
          {result.total != null
            ? t("itAdmin.ticketSearch.results.capped", {
                shown: count(result.items.length),
                total: count(result.total),
              })
            : t("itAdmin.ticketSearch.results.cappedUnknown", {
                shown: count(result.items.length),
              })}
        </Alert>
      ) : null}
      <Notes result={result} />
      {exported !== null ? (
        <Typography variant="caption" tone="muted" role="status">
          {t("itAdmin.ticketSearch.results.exported", { count: exported })}
        </Typography>
      ) : null}
      <DataGrid
        aria-label={t("itAdmin.ticketSearch.results.tableLabel")}
        columns={columns}
        data={shown}
        getRowId={(row) => row.ticket_id}
        tableLayout="fixed"
        density="compact"
        sort={sort}
        onSortChange={(next) => {
          setSort(next);
          setPage(1);
        }}
      />
      {sorted.length > PAGE_SIZE ? (
        <Pagination
          page={page}
          total={sorted.length}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          label={tc("pagination.label")}
          prevLabel={tc("pagination.previous")}
          nextLabel={tc("pagination.next")}
          formatStatus={({ page: current, pageCount }) =>
            tc("pagination.status", { page: count(current), pageCount: count(pageCount) })
          }
          formatSummary={(range) =>
            tc("pagination.summary", {
              from: count(range.from),
              to: count(range.to),
              total: count(range.total),
            })
          }
        />
      ) : null}
    </div>
  );
}

const KIND_ONLY = ["priority", "category", "offering"] as const;
type KindOnly = (typeof KIND_ONLY)[number];

/** What the keywords were matched against, and filters that left one kind out. */
function Notes({ result }: { result: TicketSearchResponse }) {
  const { t } = useT("tickets");
  const coverage = result.coverage ?? [];
  const kindOnly = result.kind_only_filters ?? [];
  if (coverage.length === 0 && kindOnly.length === 0) {
    return null;
  }
  const parts: string[] = [];
  if (coverage.includes("incident_text")) {
    parts.push(t("itAdmin.ticketSearch.coverage.incident"));
  }
  if (coverage.includes("request_subject")) {
    parts.push(t("itAdmin.ticketSearch.coverage.request"));
  }
  if (coverage.includes("notes")) {
    parts.push(t("itAdmin.ticketSearch.coverage.notes"));
  }
  if (coverage.includes("notes_capped")) {
    parts.push(t("itAdmin.ticketSearch.coverage.notesCapped"));
  }
  return (
    <div className="flex flex-col gap-1">
      {parts.length > 0 ? (
        <Typography variant="caption" tone="muted">
          {t("itAdmin.ticketSearch.coverage.label")} {parts.join(" · ")}
        </Typography>
      ) : null}
      {kindOnly
        .filter((name): name is KindOnly => KIND_ONLY.includes(name as KindOnly))
        .map((name) => (
          <Typography key={name} variant="caption" tone="muted">
            {t(`itAdmin.ticketSearch.kindOnly.${name}`)}
          </Typography>
        ))}
    </div>
  );
}
